import { useAuthUserContext } from 'support/auth/AuthUserContext';
import { AuthenticationUserFragment, RoleName } from 'types/graphql';

import { ResourceName, TeamResourceName } from './ResourceName';

interface UseHasAccessResult {
  roleName: RoleName | null;
  hasAccess: boolean;
}

export type ActionName =
  | 'create:track'
  | 'create_hotstart_pool:team'
  | 'delete:track'
  | 'delete_invite:team'
  | 'delete_user:team'
  | 'invite_user:team'
  | 'read:team'
  | 'read_api_key:team'
  | 'read_arcades:team'
  | 'read_hotstart_pools:team'
  | 'read_invites:team'
  | 'read_members:team'
  | 'read_statistics:team'
  | 'read_track_invites:team'
  | 'read_secrets:team'
  | 'update:team'
  | 'update:track'
  | 'update_hotstart_pool:team'
  | 'update_secrets:team'
  | 'update_play_limit:track'
  | 'update_role:team'
  | 'webhook_admin:team'
  | 'read_sso_connection:team'
  | 'list:sandbox'
  | 'update-ttl:sandbox'
  | 'stop:sandbox'
  | 'read:sandbox-config'
  | 'update:sandbox-config';

const ALL_ACTIONS: ActionName[] = [
  'create:track',
  'create_hotstart_pool:team',
  'delete:track',
  'delete_invite:team',
  'delete_user:team',
  'invite_user:team',
  'read:team',
  'read_api_key:team',
  'read_arcades:team',
  'read_hotstart_pools:team',
  'read_invites:team',
  'read_members:team',
  'read_statistics:team',
  'read_track_invites:team',
  'read_secrets:team',
  'update_secrets:team',
  'update:team',
  'update:track',
  'update_hotstart_pool:team',
  'update_play_limit:track',
  'update_role:team',
  'webhook_admin:team',
  'read_sso_connection:team',
  'list:sandbox',
  'update-ttl:sandbox',
  'stop:sandbox',
  'read:sandbox-config',
  'update:sandbox-config',
];

const GRANTED_ACTIONS_BY_ROLE: Record<RoleName, ActionName[]> = {
  owner: ALL_ACTIONS,
  content_creator: [
    'create:track',
    'create_hotstart_pool:team',
    'delete:track',
    'read:team',
    'read_members:team',
    'read_hotstart_pools:team',
    'read_invites:team',
    'read_statistics:team',
    'read_track_invites:team',
    'update:track',
    'update_hotstart_pool:team',
    'list:sandbox',
    'update-ttl:sandbox',
    'stop:sandbox',
    'read:sandbox-config',
    'update:sandbox-config',
  ],
  member: ['read:team', 'read_members:team', 'read_statistics:team', 'update:track'],
};

/**
 * Returns whether the currently signed in user can perform the given action on a resource name.
 *
 * @param action The action to perform.
 * @param resourceName The resource name of the target entity.
 */
export function useHasAccess(action: ActionName, resourceName: ResourceName): UseHasAccessResult {
  const { instruqtUser } = useAuthUserContext();

  if (!instruqtUser || !instruqtUser.teams) {
    return createDeniedResult();
  }

  const orgIDOrSlug = resourceName.getTeamIDOrSlug();

  if (orgIDOrSlug === TeamResourceName.ANY) {
    const firstAccessibleTeam = instruqtUser.teams.find(team => {
      return hasAccess(action, team.role, instruqtUser.is_global_admin);
    });

    if (firstAccessibleTeam) {
      return {
        roleName: firstAccessibleTeam.role,
        hasAccess: true,
      };
    }

    return createDeniedResult();
  }

  const roleName = getTeamRole(orgIDOrSlug, instruqtUser);

  return {
    roleName,
    hasAccess: hasAccess(action, roleName, instruqtUser.is_global_admin),
  };
}

const createDeniedResult = (): UseHasAccessResult => ({
  roleName: null,
  hasAccess: false,
});

const getTeamRole = (teamIDOrSlug?: string, user?: AuthenticationUserFragment): RoleName | null => {
  return user?.teams?.find(({ node }) => node?.id === teamIDOrSlug || node?.slug === teamIDOrSlug)?.role || null;
};

const hasAccess = (action: ActionName, role: RoleName | null, isGlobalAdmin: boolean | null) => {
  return (isGlobalAdmin && ALL_ACTIONS.includes(action)) || (!!role && GRANTED_ACTIONS_BY_ROLE[role].includes(action));
};
